
import React from 'react';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { OverallScoreCard } from './OverallScoreCard';
import { DetailedScoresList } from './DetailedScoresList';
import { CategoryScoreGrid } from './CategoryScoreGrid';
import { AIReadinessScoreBlock } from './AIReadinessScoreBlock';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AnalysisScoresDisplayProps {
  pageAnalysisResult: DeepAnalysisResult;
}

export const AnalysisScoresDisplay: React.FC<AnalysisScoresDisplayProps> = ({
  pageAnalysisResult
}) => {
  // Safety check - if pageAnalysisResult is null/undefined, provide default values
  if (!pageAnalysisResult) {
    return (
      <Alert variant="destructive" className="my-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Error: Analysis result is missing or invalid
        </AlertDescription>
      </Alert>
    );
  }

  // Safety check - if category_scores is undefined, use empty object
  const categoryScores = pageAnalysisResult.category_scores || {};
  
  // Debug logging to track AI Readiness score
  console.log('🚨 AnalysisScoresDisplay RECEIVED pageAnalysisResult:', {
    overall_score: pageAnalysisResult.overall_score,
    category_scores: pageAnalysisResult.category_scores,
    ai_optimization_present: 'ai_optimization_score' in (pageAnalysisResult.category_scores || {})
  });
  console.log('🚨 AnalysisScoresDisplay categoryScores being passed to CategoryScoreGrid:', categoryScores);
  console.log('AI Optimization Score present in categoryScores:', 'ai_optimization_score' in categoryScores);
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="col-span-1">
          <OverallScoreCard 
            score={pageAnalysisResult.overall_score || 0} 
            error={pageAnalysisResult.error}
          />
        </div>
        <div className="col-span-1 lg:col-span-2">
          <CategoryScoreGrid categoryScores={categoryScores} />
        </div>
      </div>
      
      <div className="pt-2 sm:pt-4">
        <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">Detailed Scores</h3>
        <div className="border border-teal-500/20 rounded-lg bg-gradient-to-br from-teal-500/5 to-emerald-500/5 p-3 sm:p-4 space-y-3 sm:space-y-4">
          <AIReadinessScoreBlock pageAnalysisResult={pageAnalysisResult} />
          <DetailedScoresList pageAnalysisResult={pageAnalysisResult} />
        </div>
      </div>
    </div>
  );
};
